import React, {
  useState,
  useEffect,
  useContext,
  FC,
  Dispatch,
  SetStateAction
} from 'react'
import { Trans, t } from '@lingui/macro'
import { debounce, cloneDeep, findIndex } from 'lodash'
import { Box } from '@chakra-ui/react'
import classNames from 'classnames'
import ReactQuill from '@capaj/react-quill'

import {
  TopicElementModelAsEditor,
  QuizOptionModelAsEditor
} from 'front-end/src/graphql/private/PrivateSchemaTypes'
import { DangerouslySetInnerHTML } from 'front-end/src/utils/DangerouslySetInnerHTML'
import { NetworkStatusContext } from 'front-end/src/components/private-route/PrivateRoute'
import { Tooltip, Flex, Checkbox, Radio } from 'front-end/src/components'

import EditorQuill from '../../../../components/common/quill/EditorQuill'
import { useLooopTheme } from '../../../../public-api/hooks/useLooopTheme'
import { useTopicForEditQuery } from '../../../topic-edit-page/TopicEditPage'
import { fileUploadInTopics } from '../../../../components/utils/file-upload/fileUploadInTopics'
import { TopicElementTypeEnum } from 'types/TopicElementTypeEnum'
import { apolloClientPrivate } from '../../../../graphql/apolloClient'
import IconClose from '../../../../components/common/icon/IconClose'
import { patchElementContent } from '../utils'
import {
  usePatchQuizOptionMutation,
  ChangeQuizTypeMutationFn,
  ChangeQuizTypeDocument,
  SetLengthMutation,
  SetLengthMutationVariables,
  SetLengthDocument,
  AddOptionMutationFn,
  AddOptionDocument,
  DeleteOptionMutationFn,
  DeleteOptionDocument
} from './QuizEditMode.codegen'
import { FreeQuizNote } from '../../topic-elements/quiz-components/FreeQuizNote'
import { QuizOption } from '../../topic-elements/quiz-components/QuizOption'
import { QuizElementType } from '../../constants/QuizElementTypes'
import { brandColors } from 'shared/basicPalette'
import { fontSizes } from 'front-end/src/components/chakraThemeExtension'
import { css } from '@emotion/react'
import { isColorDark } from 'shared/colorUtils'
import { TopicEditingContext } from '../../../topic-edit-page/TopicEditContext'

const INLINE_QUILL = {
  toolbar: [['bold', 'italic', 'underline']]
}

interface IQuizEditModeProps {
  element: TopicElementModelAsEditor
  editing: boolean
  setEditing: Dispatch<SetStateAction<boolean>>
  content: string
  setContent: (content: string) => void
}

interface IFreeQuizLength {
  minLength: number
  maxLength: number
}

const debounced = debounce((f) => f(), 1000)

export const QuizEditMode: FC<IQuizEditModeProps> = ({
  element,
  editing,
  setEditing,
  content,
  setContent
}) => {
  const { type, quizOptions } = element

  const [length, setLength] = useState<IFreeQuizLength>({
    minLength: Number.isInteger(element.data?.minLength)
      ? element.data?.minLength
      : 50,
    maxLength: element.data?.maxLength || 280
  })

  const [quizOptionsState, setQuizOptionsState] = useState(quizOptions)
  const [showDropdown, setShowDropdown] = useState(false)

  const elementType = type as any as TopicElementTypeEnum
  useEffect(() => {
    setQuizOptionsState(quizOptions)
  }, [quizOptions])

  return editing ? (
    <QuizEditing
      elementType={elementType}
      html={content}
      setHtml={setContent}
      length={length}
      setLength={setLength}
      element={element}
      quizOptionsState={quizOptionsState}
      setQuizOptionsState={setQuizOptionsState}
      showDropdown={showDropdown}
      setShowDropdown={setShowDropdown}
      setEditing={setEditing}
    />
  ) : (
    <QuizPreview
      html={content}
      elementType={elementType}
      length={length}
      quizOptionsState={quizOptionsState}
    />
  )
}

interface QuizPreviewProps {
  html: string
  length: IFreeQuizLength
  elementType: TopicElementTypeEnum
  quizOptionsState: QuizOptionModelAsEditor[]
}

const QuizPreview = ({
  elementType,
  html,
  length,
  quizOptionsState
}: QuizPreviewProps) => {
  const isSingle = elementType == TopicElementTypeEnum.QUIZ_SINGLE
  const isMulti = elementType == TopicElementTypeEnum.QUIZ_MULTI
  const isOpen = elementType == TopicElementTypeEnum.QUIZ_OPEN
  const isFree = elementType == TopicElementTypeEnum.QUIZ_FREE

  const className = classNames(
    'QuizElement QuizElement__mode-learn QuizElement-edit',
    {
      'QuizElement__type-single': isSingle,
      'QuizElement__type-multi': isMulti,
      'QuizElement__type-open': isOpen
    }
  )

  return (
    <div className={className}>
      <div className="QuizElement__question">
        <DangerouslySetInnerHTML content={html} />
      </div>
      <Box fontSize={fontSizes.sm} mb="24px" color={brandColors.grey[500]}>
        {QuizElementType[elementType]}
      </Box>
      <div className="QuizElement__answer">
        {isFree ? (
          <>
            <textarea
              className="QuizElement__answer__free form-textAreaInput"
              placeholder={t`Your response...`}
              disabled={false}
              defaultValue={''}
            />
            <FreeQuizNote
              currentLength={0}
              minLength={length.minLength}
              maxLength={length.maxLength}
            />
          </>
        ) : (
          quizOptionsState.map((option) => {
            return (
              <QuizOption
                key={option.id}
                option={option}
                isSelected={option.is_correct}
                isMultiChoice={isMulti}
                isOpen={isOpen}
                existingOptionResponse={null}
                quizResponse={null}
                optionClickHandler={() => {}}
                allowRetry={false}
              />
            )
          })
        )}
      </div>
    </div>
  )
}

interface QuizEditingProps {
  elementType: TopicElementTypeEnum
  element: TopicElementModelAsEditor
  html: string
  setHtml: (html: string) => void
  length: IFreeQuizLength
  setLength: (l: IFreeQuizLength) => void
  quizOptionsState: QuizOptionModelAsEditor[]
  setQuizOptionsState: (s: QuizOptionModelAsEditor[]) => void
  showDropdown: boolean
  setShowDropdown: (s: boolean) => void
  setEditing: Dispatch<SetStateAction<boolean>>
}

const QuizEditing = ({
  elementType,
  html,
  setHtml,
  element,
  length,
  setLength,
  quizOptionsState,
  setQuizOptionsState,
  showDropdown,
  setShowDropdown,
  setEditing
}: QuizEditingProps) => {
  const { theme } = useLooopTheme()
  const isSingle = elementType == TopicElementTypeEnum.QUIZ_SINGLE
  const isMulti = elementType == TopicElementTypeEnum.QUIZ_MULTI
  const isOpen = elementType == TopicElementTypeEnum.QUIZ_OPEN
  const isFree = elementType == TopicElementTypeEnum.QUIZ_FREE

  const status = useContext(NetworkStatusContext)

  const [debounceList] = useState(
    Array.from(Array(50).keys()).map(() => debounce((f: () => any) => f(), 500))
  )
  const [patchQuizOption] = usePatchQuizOptionMutation()

  const topicEditingCtx = useContext(TopicEditingContext)
  const { topic } = topicEditingCtx
  const { refetch: refetchTopic } = useTopicForEditQuery()

  const setFeedback = (index: number, feedback: string | null) => {
    const optionsStateCopy = cloneDeep(quizOptionsState)
    const option = optionsStateCopy[index]
    option.feedback = feedback
    setQuizOptionsState(optionsStateCopy)

    debounced(async () => {
      await patchQuizOption({
        variables: {
          topicId: topic.id,
          elementId: element.id,
          quizOptionId: option.id,
          input: { feedback }
        }
      })
    })
  }

  const addFeedbackToOption = (index: number) => () => {
    setFeedback(index, '')
  }

  const deleteOptionFeedback = (index: number) => () => {
    setFeedback(index, null)
  }

  const setFeedbackContent = (index: number, content: string) => {
    setFeedback(index, content)
  }

  const changeType = (type: string) => async (e) => {
    e.stopPropagation()
    // TODO: [LOW] Get rid of refetching whole topic here after mutation.
    // Actually we use some of topic refetch data like pass_mark that might change during quiz type change!

    await apolloClientPrivate.mutate<ChangeQuizTypeMutationFn>({
      mutation: ChangeQuizTypeDocument,
      variables: {
        topicId: topic.id,
        elementId: element.id,
        type
      }
    })
    await refetchTopic()

    setShowDropdown(false)
  }

  const setOptionContent = (
    index: number,
    content: string,
    debounceFn: (f: () => any) => any
  ) => {
    const optionsStateCopy = cloneDeep(quizOptionsState)
    const option = optionsStateCopy[index]
    option.content = content
    setQuizOptionsState(optionsStateCopy)

    debounceFn(async () => {
      await patchQuizOption({
        variables: {
          topicId: topic.id,
          elementId: element.id,
          quizOptionId: option.id,
          input: { content }
        }
      })
    })
  }

  const updateLengths = (newLength: IFreeQuizLength) => async () => {
    if (
      Number.isInteger(newLength.minLength) &&
      Number.isInteger(newLength.maxLength)
    ) {
      await apolloClientPrivate.mutate<
        SetLengthMutation,
        SetLengthMutationVariables
      >({
        mutation: SetLengthDocument,
        variables: {
          ...newLength,
          topicId: topic.id,
          elementId: element.id
        }
      })
      await refetchTopic()
    }
  }

  const addOption = async () => {
    // TODO: [LOW] Get rid of refetching whole topic here after mutation.

    await apolloClientPrivate.mutate<AddOptionMutationFn>({
      mutation: AddOptionDocument,
      variables: {
        topicId: topic.id,
        elementId: element.id
      }
    })
    await refetchTopic()
  }

  const deleteOption = (optionId: number) => async () => {
    const optionsStateCopy = cloneDeep(quizOptionsState)
    const index = findIndex(optionsStateCopy, ['id', optionId])
    optionsStateCopy.splice(index, 1)
    setQuizOptionsState(optionsStateCopy)

    await apolloClientPrivate.mutate<DeleteOptionMutationFn>({
      mutation: DeleteOptionDocument,
      variables: {
        topicId: topic.id,
        elementId: element.id,
        optionId
      }
    })
    await refetchTopic()
  }

  const toggleCorrect = (index: number) => async () => {
    const optionsStateCopy = cloneDeep(quizOptionsState)
    const option = optionsStateCopy[index]

    // Optimistic update of FE state, it does duplicate BE logic
    // not sure it's that critical though.
    if (option.is_correct) {
      option.is_correct = false
    } else {
      if (isMulti || isOpen) {
        option.is_correct = true
      } else {
        optionsStateCopy.forEach((option) => {
          option.is_correct = false
        })
        option.is_correct = true
      }
    }

    setQuizOptionsState(optionsStateCopy)

    await patchQuizOption({
      variables: {
        topicId: topic.id,
        elementId: element.id,
        quizOptionId: option.id,
        input: {
          is_correct: option.is_correct
        }
      }
    })
    await refetchTopic()
  }
  const isUpdatingTopic = status.numPendingMutations > 0

  const handleContentChange = async (quillContent) => {
    const content = await patchElementContent(
      element,
      topic,
      quillContent,
      () => {}
    )
    setHtml(content)
  }

  return (
    <div
      className={classNames('quiz-element quiz-mode-edit', {
        'quiz-type-single': elementType == TopicElementTypeEnum.QUIZ_SINGLE,
        'quiz-type-multi': elementType == TopicElementTypeEnum.QUIZ_MULTI,
        'quiz-type-open': elementType == TopicElementTypeEnum.QUIZ_OPEN
      })}
    >
      <EditorQuill
        value={html}
        autofocus={true}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            setEditing(false)
          }
        }}
        uploadFn={async (fileName, blob) => {
          return fileUploadInTopics({
            topicId: topic.id,
            elementType: element.type,
            elementId: element.id,
            blob: blob,
            fileName,
            documentType: 'image'
          })
        }}
        onChange={handleContentChange}
      />
      {isFree && (
        <div className="quiz-free-fields">
          <div className="Quiz__field">
            <div className="Quiz__field__label">
              <Trans>Minimum answer length</Trans>
            </div>
            <input
              className="Input"
              type="number"
              min="0"
              max="280"
              value={length.minLength}
              onChange={(e) => {
                const newLength = {
                  minLength: parseInt(e.target.value),
                  maxLength: length.maxLength
                }
                setLength(newLength)
                debounced(updateLengths(newLength))
              }}
            />
          </div>
          <div className="Quiz__field">
            <div className="Quiz__field__label">
              <Trans>Maximum answer length</Trans>
            </div>
            <input
              className="Input"
              type="number"
              min="0"
              value={length.maxLength}
              onChange={(e) => {
                const newLength = {
                  minLength: length.minLength,
                  maxLength: parseInt(e.target.value)
                }
                setLength(newLength)
                debounced(updateLengths(newLength))
              }}
            />
          </div>
        </div>
      )}
      <div className="quiz-toolbar">
        <div className="checkbox-type">
          <div
            className={classNames('checkbox-type-button', {
              isOpen: showDropdown
            })}
            onClick={() => setShowDropdown(!showDropdown)}
          >
            <Trans>Quiz type</Trans>:{' '}
            {isMulti && (
              <span>
                <Trans>Multi select</Trans>
              </span>
            )}
            {isSingle && (
              <span>
                <Trans>Single select</Trans>
              </span>
            )}
            {isOpen && (
              <span>
                <Trans>Open</Trans>
              </span>
            )}
            {isFree && (
              <span>
                <Trans>Free text</Trans>
              </span>
            )}
            <span
              className={classNames('icon', {
                'icon-down': !showDropdown,
                'icon-up': showDropdown
              })}
            />
          </div>
          {showDropdown && (
            <div
              className="checkbox-type-popup"
              css={css`
                .checkbox-type-option.active {
                  background-color: ${theme.colors.buttonPrimary.default};
                  color: ${isColorDark(theme.colors.buttonPrimary.default)
                    ? brandColors.white
                    : brandColors.grey.dark};
                }
              `}
            >
              <div
                className={classNames('checkbox-type-option option-s', {
                  active: isSingle
                })}
                onClick={changeType(TopicElementTypeEnum.QUIZ_SINGLE)}
              >
                <h4>
                  <Trans>Single select</Trans>
                </h4>
                <p>
                  <Trans>
                    Users select only one option
                    <br />
                    At least one option must be marked as correct.
                  </Trans>
                </p>
                <div className="icon icon-checkmark" />
              </div>

              <div
                className={classNames('checkbox-type-option option-m', {
                  active: isMulti
                })}
                onClick={changeType(TopicElementTypeEnum.QUIZ_MULTI)}
              >
                <h4>
                  <Trans>Multi select</Trans>
                </h4>
                <p>
                  <Trans>
                    Users select one or more options
                    <br />
                    At least one option must be marked as correct.
                  </Trans>
                </p>
                <div className="icon icon-checkmark" />
              </div>

              <div
                className={classNames('checkbox-type-option option-o', {
                  active: isOpen
                })}
                onClick={changeType(TopicElementTypeEnum.QUIZ_OPEN)}
              >
                <h4>
                  <Trans>Open</Trans>
                </h4>
                <p>
                  <Trans>
                    Users select one or more options
                    <br />
                    There is no correct/incorrect answer.
                  </Trans>
                </p>
                <div className="icon icon-checkmark" />
              </div>

              <div
                className={classNames('checkbox-type-option option-o', {
                  active: isFree
                })}
                onClick={changeType(TopicElementTypeEnum.QUIZ_FREE)}
              >
                <h4>
                  <Trans>Free text</Trans>
                </h4>
                <p>
                  <Trans>
                    Users can respond to a question using an open text input
                    area.
                  </Trans>
                </p>
                <div className="icon icon-checkmark" />
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="quiz-answer">
        {isFree ? null : ( // ></textarea> //   disabled //   placeholder="User's response" //   className="form-textAreaInput" // <textarea
          <>
            {quizOptionsState.map((option, index) => (
              <div
                key={option.id}
                className={classNames('quiz-option', {
                  'quiz-option-correct': option.is_correct,
                  'quiz-option-selected': option.is_correct
                })}
              >
                <Flex>
                  {isMulti || isOpen ? (
                    <Checkbox
                      mr="12px"
                      size="md"
                      isChecked={!isOpen && option.is_correct}
                      isDisabled={isOpen ? true : false}
                      onChange={toggleCorrect(index)}
                    />
                  ) : (
                    <Radio
                      variant="withTickbox"
                      size="md"
                      mr="12px"
                      isChecked={option.is_correct}
                      onChange={toggleCorrect(index)}
                    />
                  )}
                  <Box flexGrow={1}>
                    <ReactQuill
                      theme="bubble"
                      modules={INLINE_QUILL}
                      key={`option-quill-${option.id}`}
                      placeholder={t`Start typing here...`}
                      value={option.content || ''}
                      onChange={(quillContent) => {
                        setOptionContent(
                          index,
                          quillContent,
                          debounceList[index]
                        )
                      }}
                    />
                  </Box>
                </Flex>
                {typeof option.feedback === 'string' && (
                  <div className="quiz-option-feedback-wrapper">
                    <div className="quiz-option-feedback">
                      <a
                        className="quiz-option-feedback-clear"
                        onClick={deleteOptionFeedback(index)}
                      >
                        <IconClose />
                      </a>
                      <div className="quiz-option-feedback-arrow" />
                      <ReactQuill
                        className="sp quiz-option-feedback-text edit"
                        theme="bubble"
                        modules={INLINE_QUILL}
                        placeholder={t`Start typing here...`}
                        value={option.feedback || ''}
                        onChange={(quillContent) => {
                          setFeedbackContent(index, quillContent)
                        }}
                      />
                    </div>
                  </div>
                )}
                {isUpdatingTopic === false && (
                  <div className="delete-option">
                    {quizOptionsState.length > 1 && (
                      <Tooltip text={<Trans>Delete</Trans>}>
                        <div
                          className="icon icon-close"
                          onClick={deleteOption(option.id)}
                        />
                      </Tooltip>
                    )}
                  </div>
                )}
                <div className="feedback-option">
                  {typeof option.feedback !== 'string' && (
                    <Tooltip text={<Trans>Add feedback</Trans>}>
                      <div
                        className="icon icon-speech clickable"
                        onClick={addFeedbackToOption(index)}
                      />
                    </Tooltip>
                  )}
                </div>
              </div>
            ))}
            <a
              onClick={() => isUpdatingTopic === false && addOption()}
              className={classNames('quiz-option-add', {
                disabled: isUpdatingTopic
              })}
            >
              <span className="icon icon-plus" />
              <span className="label">
                <Trans>Add another option</Trans>
              </span>
            </a>
          </>
        )}
      </div>
    </div>
  )
}
